import React, { useState } from "react";

import { useScripts } from "../../../../services/useScripts";
import SessionChoiceLine from "../../../../components/shared/SessionChoiceLine";

/**
 * Workstation · Ads & Creative — **step 4, Script Generation, in v2.**
 *
 * WAVE 3, SCREEN 4. Behaviour is `services/useScripts`, shared with v1.
 *
 * The sweep's findings are in that module. The one worth repeating here is the
 * one unique to this screen: `fetchScripts` depended on `[offer.offer_id]` while
 * reading `angleId`, so after MA-3 gave each track its own angle, **track 2
 * would have re-read track 1's scripts** — silently, with no error, only on a
 * re-fetch.
 *
 * FOUNDER RULING 2026-09-09 — THE TWO DROPDOWNS ARE GONE. This step asked for a
 * platform and a length of its own, which is the repetition he asked to be
 * removed: "Pick the format and length once, up front, and every later step
 * should just use that stored choice rather than asking again." Worse than
 * repetition, they could DISAGREE — a session formatted "TikTok — 9:16" could
 * have its scripts written for META, because nothing tied the two answers
 * together. The session's format now decides the platform and the session's
 * length decides the structure, both resolved server-side, and this screen shows
 * what was chosen instead of asking.
 */
export default function ScriptStep({ offer, sessionId, isNewSession,
                                     sessionStartTime, angleId, refreshKey,
                                     sessionMeta, onGoToStep, onComplete }) {
  const S = useScripts({ offer, sessionId, isNewSession, sessionStartTime,
                         angleId, onComplete, refreshKey });

  const [open, setOpen] = useState(null);            // expanded script_id
  const [pushKey, setPushKey] = useState(null);      // `${id}::${section}`
  const [pushText, setPushText] = useState("");
  // `{ ok, text }` per section — an explanation and a failure are DIFFERENT
  // shapes here, so a failed read cannot render as if it were reasoning.
  const [explained, setExplained] = useState({});
  const [editKey, setEditKey] = useState(null);     // `${id}::${section}`
  const [editDraft, setEditDraft] = useState({});
  const [names, setNames] = useState({});           // script_id -> name payload
  const [confirmFailures, setConfirmFailures] = useState([]);

  const openEdit = (id, sec) => {
    setEditKey(`${id}::${sec.section}`);
    // Seeded from the section, so an untouched field round-trips unchanged
    // rather than being blanked by an empty form.
    setEditDraft({
      spoken_text: sec.spoken_text || "",
      visual_direction: sec.visual_direction || "",
      on_screen_text: sec.on_screen_text || "",
    });
  };

  const showName = async (id) => {
    setNames((p) => ({ ...p, [id]: { loading: true } }));
    // Resolved BEFORE the updater. `await` inside the updater arrow is a
    // syntax error — the arrow is not async — and it would have shipped as a
    // build failure rather than a subtle one.
    const res = await S.loadName(id);
    setNames((p) => ({ ...p, [id]: res }));
  };

  /**
   * Confirm every unconfirmed script, then move on — the same gesture step 1
   * and step 3 use.
   *
   * IT DOES NOT ADVANCE ON A PARTIAL RESULT. A script that did not get its
   * permanent number would be named `B1` in production, silently, and moving
   * the operator forward would hide that. The refusals are named instead.
   */
  const confirmAndContinue = async () => {
    const res = await S.confirmAll();
    setConfirmFailures(res.failures);
    if (res.clean && S.proceed) S.proceed();
  };

  const explain = async (id, section) => {
    const key = `${id}::${section}`;
    setExplained((p) => ({ ...p, [key]: { loading: true } }));
    const res = await S.explainSection(id, section);
    setExplained((p) => ({ ...p, [key]: res }));
  };

  /**
   * A script's sections, in the shape the database actually stores.
   *
   * THE DEFECT THIS FIXES, found live by the Founder: the port read
   * `x.content || x.text`. **Neither field exists.** A section is
   * `{ section, timing, spoken_text, visual_direction }`, so every section
   * rendered its name and its buttons with an EMPTY body — the script itself
   * was invisible on the screen whose job is to show it.
   *
   * I guessed the field names instead of reading a stored script. The names are
   * taken from production now, and `scriptStep.test.jsx` pins them against a
   * real row so a rename cannot silently blank the screen again.
   */
  const sectionsOf = (script) =>
    Array.isArray(script.sections) ? script.sections : [];

  return (
    <div className="ws-step" data-testid="ws-script-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Scripts</h3>
          <p className="dim">
            Review each script and rewrite any section that is not right. The
            platform, the length and the creative type all come from the choice
            made on step 1 — this step does not ask again.
          </p>
        </div>
        <div className="ws-script-controls">
          <button type="button"
                  className={S.scripts.length === 0 ? "btn-primary btn-sm" : "btn-ghost btn-sm"}
                  onClick={S.generateScripts}
                  disabled={S.generating || S.loadState === "loading"}
                  data-testid="ws-script-generate">
            {S.generating ? "Generating…" : S.scripts.length === 0 ? "Generate Scripts" : "Generate more"}
          </button>
        </div>
      </div>

      {/* What step 1 decided, read-only. No control here can disagree with it. */}
      <SessionChoiceLine sessionMeta={sessionMeta} onGoToStep={onGoToStep}
                         testidPrefix="ws-script-choice" />

      {!sessionMeta?.video_length ? (
        <p className="dim ws-step-note" data-testid="ws-script-length-default">
          No length was chosen for this session, so scripts are written at 30–45
          seconds — Cipher's default, not a choice anyone made. Said out loud
          because the structure of the script depends on it.
        </p>
      ) : null}

      {S.loadState === "loading" && S.scripts.length === 0 ? (
        <div className="state ws-state" data-testid="ws-script-loading">
          Reading this offer's scripts…
        </div>
      ) : null}

      {S.loadState === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-script-load-failed">
          <p>The scripts could not be read: {S.loadError}</p>
          <p className="dim">This is a failure, not an empty offer. Nothing has been lost.</p>
          <button type="button" className="btn-ghost btn-sm" onClick={S.retryLoad}
                  data-testid="ws-script-retry">Try again</button>
        </div>
      ) : null}

      {S.loadState === "ok" && S.scripts.length === 0 ? (
        <div className="state ws-state" data-testid="ws-script-empty">
          {S.allCount > 0
            ? `This session has no scripts yet. The offer holds ${S.allCount} from earlier work, not shown here.`
            : "No scripts yet. Press Generate — the platform and length come from step 1."}
        </div>
      ) : null}

      {S.note ? (
        <p className="ws-step-note" role="status" data-testid="ws-script-note">
          {S.note.text}
          {S.note.count > 1 ? <span className="dim"> (asked {S.note.count} times)</span> : null}
        </p>
      ) : null}

      {S.actionError ? (
        <p className="ws-step-note state-error" role="alert" data-testid="ws-script-error">
          {S.actionError}
        </p>
      ) : null}

      {S.scripts.length > 0 ? (
        <ul className="ws-script-list">
          {S.scripts.map((script, i) => {
            const id = script.script_id;
            const isOpen = open === id;
            return (
              <li key={id} className="ws-script" data-testid={`ws-script-${id}`}>
                <button type="button" className="ws-script-head"
                        onClick={() => setOpen(isOpen ? null : id)}
                        aria-expanded={isOpen}
                        data-testid={`ws-script-toggle-${id}`}>
                  <span className="ws-script-n">
                    {script.body_element_number || `Script ${i + 1}`}
                  </span>
                  <span className="ws-script-meta dim">
                    {script.platform}
                    {script.total_duration_seconds ? ` · ${script.total_duration_seconds}s` : ""}
                  </span>
                  {/* The stored field is still `approved_for_production` — an
                      agent tool projects it, so it is not renamed to match a
                      label (rule 8). What it MEANS is confirmed: this script's
                      permanent numbers are minted and it is saved to Drive. */}
                  {script.approved_for_production ? (
                    <span className="ws-chip" data-testid={`ws-script-approved-${id}`}>confirmed</span>
                  ) : null}
                  {script.compliance_flags?.length ? (
                    <span className="ws-chip is-warn" data-testid={`ws-script-flags-${id}`}>
                      {script.compliance_flags.length} compliance note
                      {script.compliance_flags.length > 1 ? "s" : ""}
                    </span>
                  ) : null}
                  {!script.session_id ? (
                    <span className="ws-chip is-quiet"
                          data-testid={`ws-script-unstamped-${id}`}
                          title="Written before scripts recorded their session">
                      session unknown
                    </span>
                  ) : null}
                </button>

                {isOpen ? (
                  <div className="ws-script-body">
                    {sectionsOf(script).length === 0 ? (
                      <p className="dim" data-testid={`ws-script-nosections-${id}`}>
                        This script has no sections recorded. That is the stored
                        shape, not a failure to read it.
                      </p>
                    ) : sectionsOf(script).map((sec) => {
                      const name = sec.section;
                      const key = `${id}::${name}`;
                      const ex = explained[key];
                      const edited = script.section_edits?.[name]?.last_edited_at;
                      return (
                        <section key={name} className="ws-script-section"
                                 data-testid={`ws-script-section-${id}-${name}`}>
                          <h4 className="ws-script-section-name">
                            <span className={`ws-sec-tag sec-${String(name).toLowerCase().replace(/[^a-z]/g, "")}`}>
                              {name}
                            </span>
                            {sec.timing ? <span className="dim"> {sec.timing}</span> : null}
                            {edited ? (
                              <span className="ws-chip" data-testid={`ws-script-edited-${id}-${name}`}>
                                edited
                              </span>
                            ) : null}
                            {/* FOUNDER RULING 2026-09-09: one CTA, and it is
                                the chosen one. The scriptwriter no longer
                                writes its own; a CTA section here is either the
                                selected element (new scripts) or a pre-ruling
                                draft that production will replace. */}
                            {String(name).toUpperCase() === "CTA" ? (
                              <span className="dim ws-sec-hint"
                                    data-testid={`ws-script-cta-hint-${id}`}>
                                {sec.cta_element_number
                                  ? `— ${sec.cta_element_number}, chosen on the CTA step`
                                  : "— written before the CTA step existed. "
                                    + "Production will use the CTA you choose on step 5."}
                              </span>
                            ) : null}
                          </h4>
                          <p className="ws-script-section-text"
                             data-testid={`ws-script-spoken-${id}-${name}`}>
                            {sec.spoken_text}
                          </p>
                          {/* 72 sections in production carry on-screen text
                              and NEITHER surface rendered it — real ad copy,
                              invisible. Found while confirming the field names
                              across all 289 scripts. */}
                          {sec.on_screen_text ? (
                            <p className="ws-script-onscreen"
                               data-testid={`ws-script-onscreen-${id}-${name}`}>
                              On screen: {sec.on_screen_text}
                            </p>
                          ) : null}
                          {sec.visual_direction ? (
                            <p className="ws-script-visual"
                               data-testid={`ws-script-visual-${id}-${name}`}>
                              Visual: {sec.visual_direction}
                            </p>
                          ) : null}
                          <div className="ws-script-section-actions">
                            <button type="button" className="btn-ghost btn-sm"
                                    onClick={() => { setPushKey(pushKey === key ? null : key); setPushText(""); }}
                                    data-testid={`ws-script-rewrite-toggle-${id}-${name}`}>
                              {pushKey === key ? "Cancel" : "Rewrite"}
                            </button>
                            <button type="button" className="btn-ghost btn-sm"
                                    onClick={() => explain(id, name)}
                                    data-testid={`ws-script-explain-${id}-${name}`}>
                              Why this?
                            </button>
                            {/* Direct editing. Rewrite is still the encouraged
                                default; this is for when the operator already
                                knows the shot — the Founder had no way to touch
                                visual direction at all. */}
                            <button type="button" className="btn-ghost btn-sm"
                                    onClick={() => (editKey === key
                                      ? setEditKey(null) : openEdit(id, sec))}
                                    data-testid={`ws-script-edit-toggle-${id}-${name}`}>
                              {editKey === key ? "Cancel" : "Edit text"}
                            </button>
                          </div>

                          {ex?.loading ? (
                            <p className="dim">Reading the reasoning…</p>
                          ) : ex && ex.ok ? (
                            <p className="ws-script-explanation"
                               data-testid={`ws-script-explanation-${id}-${name}`}>{ex.text}</p>
                          ) : ex ? (
                            // A FAILURE, styled as one. The old screen wrote
                            // "Could not load reasoning right now." into the
                            // same slot the real explanation renders in.
                            <p className="state-error" role="alert"
                               data-testid={`ws-script-explain-failed-${id}-${name}`}>
                              The reasoning could not be read: {ex.text}
                            </p>
                          ) : null}

                          {editKey === key ? (
                            <div className="ws-script-edit"
                                 data-testid={`ws-script-edit-drawer-${id}-${name}`}>
                              <label>
                                <span className="dim">Spoken line</span>
                                <textarea rows={2} value={editDraft.spoken_text}
                                          onChange={(e) => setEditDraft((d) => ({ ...d, spoken_text: e.target.value }))}
                                          data-testid={`ws-script-edit-spoken-${id}-${name}`} />
                              </label>
                              <label>
                                <span className="dim">Visual direction</span>
                                <textarea rows={2} value={editDraft.visual_direction}
                                          onChange={(e) => setEditDraft((d) => ({ ...d, visual_direction: e.target.value }))}
                                          data-testid={`ws-script-edit-visual-${id}-${name}`} />
                              </label>
                              <label>
                                <span className="dim">On-screen text</span>
                                <textarea rows={2} value={editDraft.on_screen_text}
                                          onChange={(e) => setEditDraft((d) => ({ ...d, on_screen_text: e.target.value }))}
                                          data-testid={`ws-script-edit-onscreen-${id}-${name}`} />
                              </label>
                              <div className="ws-script-edit-foot">
                                <button type="button" className="btn-ghost btn-sm"
                                        onClick={() => setEditKey(null)}
                                        data-testid={`ws-script-edit-cancel-${id}-${name}`}>
                                  Discard
                                </button>
                                <button type="button" className="btn-primary btn-sm"
                                        onClick={async () => {
                                          const ok = await S.editSection(id, name, editDraft);
                                          if (ok) setEditKey(null);
                                        }}
                                        data-testid={`ws-script-edit-save-${id}-${name}`}>
                                  Save edits
                                </button>
                              </div>
                            </div>
                          ) : null}

                          {pushKey === key ? (
                            <div className="ws-script-rewrite">
                              <textarea rows={2} value={pushText}
                                        onChange={(e) => setPushText(e.target.value)}
                                        placeholder="What should this section do differently?"
                                        data-testid={`ws-script-rewrite-input-${id}-${name}`} />
                              <button type="button" className="btn-primary btn-sm"
                                      disabled={S.busyScript === key || pushText.trim().length < 3}
                                      onClick={async () => {
                                        const ok = await S.rewriteSection(id, name, pushText);
                                        if (ok) { setPushKey(null); setPushText(""); }
                                      }}
                                      data-testid={`ws-script-rewrite-submit-${id}-${name}`}>
                                {S.busyScript === key ? "Rewriting…" : "Rewrite section"}
                              </button>
                            </div>
                          ) : null}
                        </section>
                      );
                    })}

                    {/* THE PERMANENT NAME, and the fact that it is fixed.
                        It has been computed for briefs and filenames since the
                        naming lane and shown to the operator nowhere, so "it is
                        locked" was a claim they had to take on faith. */}
                    <div className="ws-script-name">
                      {names[id]?.loading ? (
                        <span className="dim">Reading the name…</span>
                      ) : names[id]?.ok ? (
                        <>
                          <span className="ws-script-name-value"
                                data-testid={`ws-script-name-${id}`}>
                            {names[id].name || "not named yet"}
                          </span>
                          <span className="ws-chip is-quiet"
                                data-testid={`ws-script-name-locked-${id}`}
                                title={names[id].why}>
                            locked
                          </span>
                          <span className="dim ws-sec-hint">{names[id].why}</span>
                        </>
                      ) : names[id] ? (
                        <span className="state-error" role="alert"
                              data-testid={`ws-script-name-failed-${id}`}>
                          The name could not be read: {names[id].error}
                        </span>
                      ) : (
                        <button type="button" className="btn-ghost btn-sm"
                                onClick={() => showName(id)}
                                data-testid={`ws-script-name-show-${id}`}>
                          Show the creative name
                        </button>
                      )}
                    </div>

                    {/* A new script has no CTA section until one is chosen.
                        Saying so beats a script that appears to end mid-thought. */}
                    {sectionsOf(script).length > 0
                      && !sectionsOf(script).some((x) => String(x.section).toUpperCase() === "CTA") ? (
                      <p className="dim" data-testid={`ws-script-nocta-${id}`}>
                        No call to action yet — it is chosen on the CTA step and
                        added to the script there.
                      </p>
                    ) : null}

                    {/* FOUNDER RULING 2026-09-09 (CREATIVE-NO-FOUNDER-GATE):
                        "Remove the 'Approve for production' button entirely —
                        it isn't actually gating anything today anyway, so
                        nothing is lost." Its two real side effects moved to the
                        step's own Confirm & continue, at the foot. */}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {S.hiddenOtherSession || S.hiddenUnstamped ? (
        <p className="dim ws-step-note" data-testid="ws-script-hidden-note">
          Not shown:{" "}
          {[
            S.hiddenOtherSession ? `${S.hiddenOtherSession} from other sessions on this offer` : null,
            S.hiddenUnstamped ? `${S.hiddenUnstamped} that predate session tracking (Cipher cannot tell which session made them)` : null,
          ].filter(Boolean).join("; ")}
          . This view is scoped to the current session.
        </p>
      ) : null}

      {S.predateNote > 0 ? (
        <p className="dim ws-step-note" data-testid="ws-script-predate-note">
          Showing {S.predateNote} script{S.predateNote > 1 ? "s" : ""} that
          predate session tracking — Cipher cannot tell which session made them.
        </p>
      ) : null}

      {confirmFailures.length ? (
        <div className="state ws-state state-error" role="alert"
             data-testid="ws-script-confirm-failures">
          <p>{confirmFailures.length} script(s) could not be confirmed, so this
             step did not move on:</p>
          <ul>
            {confirmFailures.map((f) => (
              <li key={f.label}><strong>{f.label}</strong> — {f.detail}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {S.scripts.length > 0 ? (
        <div className="ws-step-foot">
          <div className="ws-script-regen">
            <textarea rows={2} value={S.feedback}
                      onChange={(e) => S.setFeedback(e.target.value)}
                      placeholder="What should the next batch do differently? It will be told."
                      data-testid="ws-script-feedback" />
            <button type="button" className="btn-ghost btn-sm"
                    onClick={S.generateScripts} disabled={S.generating}
                    data-testid="ws-script-generate-more">
              {S.generating ? "Generating…" : S.feedback ? "Generate with feedback" : "Generate more"}
            </button>
          </div>
          <div className="ws-script-confirm">
            <span className="dim">
              {S.scripts.filter((s) => s.approved_for_production).length} of{" "}
              {S.scripts.length} confirmed
            </span>
            <button type="button" className="btn-primary btn-sm"
                    disabled={S.busyScript === "__all__"}
                    onClick={confirmAndContinue}
                    data-testid="ws-script-continue">
              {S.busyScript === "__all__" ? "Confirming…" : "Confirm & continue"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
